"use client";

import { useMetadata, usePreformPayment } from "@web/data/metadata";
import { useAccount } from "@web/lib/auth";
import { Price } from "@web/lib/auth";
import { useBalance, useRefreshBalance } from "@web/lib/currency";
import type { ApexAxisChartSeries, ApexOptions } from "apexcharts";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import { Activity } from "./Activity";
import { BaseBlockV2, Container } from "./DashboardUtils";
import { UserMenu } from "./UserMenu";

export interface Monitoring {
  totalMonthCost: Price;
  payAsYouGoMonthCost: Price;
  graph: [string, [string, number][]][];
  colors?: string[];
}

const ApexChart = dynamic(() => import("react-apexcharts"), {
  ssr: false,
});

const DEFAULT_CHART_COLORS = ["#5E96FF", "#84B2FF", "#A8CBFF"];
const CHART_FONT_FAMILY = "var(--font-satoshi), sans-serif";

function getRandomColor() {
  // Hue values for: #3B82F6 (blue ≈ 217°) and #c5ff11 (green ≈ 132°)
  const baseHues = [217, 132];
  const hue = baseHues[Math.floor(Math.random() * baseHues.length)];

  const saturation = 90 + Math.random() * 5; // 90–95%
  const lightness = 65 + Math.random() * 10; // 65–75%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

function clampColorChannel(value: number) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function lightenColor(color: string, amount = 0.22) {
  const normalizedColor = color.trim();

  if (normalizedColor.startsWith("#")) {
    let hex = normalizedColor.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    if (hex.length === 6) {
      const red = parseInt(hex.slice(0, 2), 16);
      const green = parseInt(hex.slice(2, 4), 16);
      const blue = parseInt(hex.slice(4, 6), 16);

      const lighterRed = clampColorChannel(red + (255 - red) * amount);
      const lighterGreen = clampColorChannel(green + (255 - green) * amount);
      const lighterBlue = clampColorChannel(blue + (255 - blue) * amount);

      return `rgb(${lighterRed}, ${lighterGreen}, ${lighterBlue})`;
    }
  }

  const rgbMatch = normalizedColor.match(
    /^rgba?\(([^,]+),\s*([^,]+),\s*([^,)+]+)(?:,\s*([^)]+))?\)$/i,
  );

  if (rgbMatch) {
    const red = Number(rgbMatch[1]);
    const green = Number(rgbMatch[2]);
    const blue = Number(rgbMatch[3]);
    const alpha = rgbMatch[4];

    const lighterRed = clampColorChannel(red + (255 - red) * amount);
    const lighterGreen = clampColorChannel(green + (255 - green) * amount);
    const lighterBlue = clampColorChannel(blue + (255 - blue) * amount);

    if (alpha !== undefined) {
      return `rgba(${lighterRed}, ${lighterGreen}, ${lighterBlue}, ${alpha.trim()})`;
    }

    return `rgb(${lighterRed}, ${lighterGreen}, ${lighterBlue})`;
  }

  const hslMatch = normalizedColor.match(
    /^hsla?\(([^,]+),\s*([^,]+)%,\s*([^,)%]+)%(?:,\s*([^)]+))?\)$/i,
  );

  if (hslMatch) {
    const hue = hslMatch[1].trim();
    const saturation = hslMatch[2].trim();
    const lightness = Number(hslMatch[3]);
    const alpha = hslMatch[4];
    const lighterLightness = Math.min(
      92,
      Number((lightness + (100 - lightness) * amount).toFixed(1)),
    );

    if (alpha !== undefined) {
      return `hsla(${hue}, ${saturation}%, ${lighterLightness}%, ${alpha.trim()})`;
    }

    return `hsl(${hue}, ${saturation}%, ${lighterLightness}%)`;
  }

  return normalizedColor;
}

function formatSeriesLabel(key: string) {
  if (key.length <= 8) return key;

  return `${key.substring(0, 3)}...${key.substring(key.length - 3)}`;
}

function toSeries(monitoring?: Monitoring): ApexAxisChartSeries {
  if (!monitoring || !Array.isArray(monitoring.graph)) return [];

  return monitoring.graph.map(([key, data]) => ({
    name: formatSeriesLabel(key),
    data: data.map(([date, requests]) => ({
      x: new Date(date).getTime(),
      y: requests,
    })),
  }));
}

export enum LogType {
  InvoicePaid = 0,
  SubscriptionRequest = 1,
  PayAsYouGoRequest = 2,
}

export type Log = {
  type: LogType;
  relatedId: string;
  price: {
    value: number;
    currency: string;
  };
  timestamp: string;
};

export function useMonitoring() {
  let metadata = useMetadata();
  return metadata.monitoring as Monitoring;
}

export function useLogs() {
  const metadata = useMetadata();
  return metadata.logs as Log[];
}

function getPriceValue(price?: Price | { value?: number | null } | null) {
  return typeof price?.value === "number" ? price.value : 0;
}

function softFormat(number?: number): string {
  if (!number) {
    return "0";
  }

  if (number > 1_000_000_000) {
    return (number / 1_000_000_000).toFixed(2).replaceAll(".00", "") + "B";
  }

  if (number > 1_000_000) {
    return (number / 1_000_000).toFixed(2).replaceAll(".00", "") + "M";
  }

  return number.toString();
}

export default function ClientDashboard() {
  let monitoring = useMonitoring();
  const preformPayment = usePreformPayment();
  const chartSeries = useMemo(() => toSeries(monitoring), [monitoring]);
  const chartColors = useMemo(
    () =>
      (monitoring?.graph ?? []).map((_, index) => {
        const baseColor =
          monitoring?.colors?.[index] ??
          DEFAULT_CHART_COLORS[index] ??
          getRandomColor();

        return lightenColor(baseColor);
      }),
    [monitoring],
  );
  const chartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "area",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        background: "transparent",
        foreColor: "#FFFFFF",
        fontFamily: CHART_FONT_FAMILY,
      },
      colors: chartColors,
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          opacityFrom: 0.28,
          opacityTo: 0.02,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: "rgba(255,255,255,0.08)",
        strokeDashArray: 5,
        xaxis: {
          lines: {
            show: false,
          },
        },
      },
      markers: {
        size: 0,
        hover: {
          sizeOffset: 4,
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontFamily: CHART_FONT_FAMILY,
        labels: {
          colors: "#FFFFFF",
        },
      },
      tooltip: {
        theme: "dark",
        style: {
          fontFamily: CHART_FONT_FAMILY,
        },
        x: {
          format: "dd MMM yyyy HH:mm",
        },
      },
      xaxis: {
        type: "datetime",
        tickAmount: 6,
        labels: {
          datetimeUTC: false,
          style: {
            colors: "#FFFFFF",
            fontFamily: CHART_FONT_FAMILY,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        title: {
          text: "Time",
          style: {
            color: "#FFFFFF",
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          style: {
            colors: "#FFFFFF",
            fontFamily: CHART_FONT_FAMILY,
          },
          formatter: (value) => softFormat(Number(value)),
        },
        title: {
          text: "Requests",
          style: {
            color: "#FFFFFF",
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      noData: {
        text: "No data",
        style: {
          color: "#FFFFFF",
          fontFamily: CHART_FONT_FAMILY,
        },
      },
    }),
    [chartColors],
  );
  const balance = useBalance();
  const refreshBalance = useRefreshBalance();
  const logs = useLogs();
  const account = useAccount();
  const accountCards = Array.isArray(account?.cards)
    ? (account?.cards ?? [])
    : [];
  const safeLogs = Array.isArray(logs) ? logs : [];
  const showPaymentWarning = accountCards.length === 0;

  if (!monitoring) return <></>;

  return (
    <>
      <Container>
        <div className="flex flex-col gap-2">
          <div
            className={`grid gap-2 ${showPaymentWarning ? "xl:grid-cols-3" : "xl:grid-cols-2"}`}
          >
            <div className="flex h-[86px] w-full items-center justify-between rounded-lg bg-[#121212] bg-opacity-60 px-5 lg:pl-8 lg:pr-8">
              <div className="h-[22px] text-white text-sm font-bold leading-tight">
                This month spent
              </div>
              <div>
                <div className="flex justify-center flex-row items-center">
                  <span className="text-white text-[26px] font-bold leading-tight tracking-[0.06em]">
                    $
                    {Number(getPriceValue(monitoring?.totalMonthCost)).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 0, // or 2 if you always want cents
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </div>
                <div className="flex text-center text-[#3B82F6] text-[10px] font-bold leading-tight flex-row items-center">
                  {`($${softFormat(
                    getPriceValue(monitoring?.payAsYouGoMonthCost),
                  )} pay-as-you-go cost)`}
                </div>
              </div>
            </div>
            <div className="flex h-[86px] w-full items-center justify-between rounded-lg bg-[#121212] bg-opacity-50 px-5 lg:pl-8 lg:pr-8">
              <div className="h-[22px] text-white text-sm font-bold leading-tight">
                Your balance
              </div>
              <div className="flex gap-4 justify-center flex-row items-center">
                <span className="text-white text-[26px] font-bold leading-tight tracking-[0.06em]">
                  {balance?.symbol || "$"}
                  {softFormat(balance.balance ?? 0)}
                </span>
                <button onClick={() => refreshBalance()}>🗘</button>
                <button
                  onClick={() => preformPayment()}
                  className="w-[111px] h-8  hover:opacity-80 bg-[#c5ff11] rounded items-center flex justify-center"
                >
                  <span className="w-[90px] h-4 text-center text-black text-xs font-bold leading-tight">
                    Add balance
                  </span>
                </button>
              </div>
            </div>
            {showPaymentWarning ? <UserMenu /> : null}
          </div>
        </div>
      </Container>

      <Container className="grid flex-grow gap-[12px] lg:grid-cols-10">
        <BaseBlockV2 className="lg:col-span-6">
          <div className="text-white lg:text-base font-extrabold leading-tight lg:pl-6 pl-4 lg:pt-6 pt-4 text-base">
            Hourly API Requests
          </div>
          <div className="overflow-hidden lg:mx-6 mx-4 lg:mt-6 mt-4 flex w-full min-w-0 justify-center">
            <ApexChart
              className="mx-auto w-full max-w-full flex-1"
              type="area"
              height={340}
              width="96%"
              options={chartOptions}
              series={chartSeries}
            />
          </div>
        </BaseBlockV2>
        <BaseBlockV2 className="lg:col-span-4">
          <div className="text-white lg:text-base font-extrabold leading-tight lg:pl-6 pl-4 lg:pt-6 pt-4 text-base">
            My activities
          </div>
          <div className="w-full lg:px-6 px-4 ">
            <div className="h-[calc(80vh-145px)] overflow-y-auto ">
              <table className="text-white text-center border-spacing-0 border-separate w-full xl:mt-8 mt-6">
                <tbody className="text-white text-center text-sm font-normal leading-tight">
                  <tr className="text-center bg-[#121212] text-sm font-bold leading-tight">
                    <th className="font-semibold py-3 whitespace-nowrap rounded-l-lg border-l border-t border-b	border-[#3B82F6]">
                      Time
                    </th>
                    <th className="font-semibold py-3 whitespace-nowrap border-t border-b	border-[#3B82F6]">
                      Type
                    </th>
                    <th className="font-semibold py-3 whitespace-nowrap border-t border-b	border-[#3B82F6]">
                      Reference
                    </th>
                    <th className="font-semibold py-3 whitespace-nowrap rounded-r-lg border-r border-t border-b	border-[#3B82F6]">
                      Price
                    </th>
                  </tr>
                  {safeLogs.map((log, idx) => (
                    <Activity log={log} key={idx} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </BaseBlockV2>
      </Container>
    </>
  );
}
